// ============================================================================
// DIALOGUES BLOQUANTS — file partagée + hôte DOM des canaux `confirm()` et `alert()` (§24.1quater).
// Pendant exact de `@orgazone/ui-native/dialog` (React Native).
//
// ⚠️ Store MODULE (hors React), comme le toast : une confirmation doit pouvoir partir d'un service ou
// d'un `catch`, pas seulement d'un composant. UN SEUL dialogue à l'écran : les demandes suivantes font
// la file (FIFO) — deux questions superposées seraient illisibles et dangereuses.
// ⚠️ Le clavier et l'ordre des boutons sont imposés ICI (Échap = annuler, Entrée = action, focus initial
// sur le bouton d'action, Annuler à gauche) : les appelants n'ont PAS à y penser, donc ne peuvent pas
// diverger (§24.3bis B-2).
// ============================================================================
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Trans } from '@orgazone/ui-web';
import {
  ALERT_DEFAULTS, ALERT_ICON, CONFIRM_DEFAULTS, CONFIRM_ICON,
  DIALOG_BUTTONS, DIALOG_CARD, DIALOG_OVERLAY, DIALOG_TILE, DIALOG_TIMING,
  type AlertTone, type ConfirmTone, type IconName,
} from '@orgazone/ui-tokens';
import { t } from '@orgazone/i18n';
/** Union interne des options des deux canaux (les contrats publics vivent dans confirm.tsx/alert.tsx). */
export interface DialogOpts {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  closeLabel?: string;
  tone?: ConfirmTone | AlertTone;
  /** Recopie exigee avant d autoriser l action — confirm SEULEMENT (contrat public : confirm.tsx). */
  requireText?: string;
}

/** Demande en attente — `respond` résout la promesse de l'appelant (true/false pour confirm). */
export interface DialogItem {
  id: number;
  kind: 'confirm' | 'alert';
  opts: DialogOpts;
  respond: (value: boolean) => void;
}

let queue: DialogItem[] = [];
const listeners = new Set<(current: DialogItem | null) => void>();
const notify = (): void => listeners.forEach((fn) => fn(queue[0] ?? null));

/** S'abonne au dialogue COURANT (l'hôte, et les tests — même motif observable que la file de toasts). */
export function subscribeDialogs(fn: (current: DialogItem | null) => void): () => void {
  listeners.add(fn);
  fn(queue[0] ?? null);
  return () => { listeners.delete(fn); };
}

/**
 * Un `<DialogHost/>` est-il monté ?
 *
 * ⚠️ EXPOSÉ pour que l'appelant puisse GARDER UN FILET, pas pour qu'il se dispense de l'hôte : sans
 * hôte, `confirm()` répond non (sûr) mais `alert()` n'écrit qu'en console — et un échec qui n'atteint
 * pas l'écran est un échec avalé (§23.0). Un composant partagé ne peut pas monter l'hôte à la place de
 * l'app ; il peut, lui, montrer l'échec autrement quand il constate qu'il ne serait entendu par personne.
 */
export function hasDialogHost(): boolean {
  return listeners.size > 0;
}

let seq = 0;
/** Empile une demande ; résolue par l'hôte (ou refusée immédiatement si AUCUN hôte n'est monté). */
export function pushDialog(kind: DialogItem['kind'], opts: DialogOpts): Promise<boolean> {
  // ⚠️ FAIL-SAFE sans hôte : une promesse qui ne se résout jamais gèlerait le flux appelant. Sans hôte,
  // on répond NON (l'action n'est pas confirmée — jamais l'inverse) et on le dit en console (§23.0).
  if (listeners.size === 0) {
    console.error(`[ui-web] ${kind}() appelé sans <DialogHost/> monté — réponse refusée par défaut.`);
    return Promise.resolve(false);
  }
  return new Promise<boolean>((resolve) => {
    const item: DialogItem = {
      id: ++seq,
      kind,
      opts,
      respond: (value) => {
        queue = queue.filter((x) => x.id !== item.id);
        notify();
        resolve(value);
      },
    };
    queue = [...queue, item];
    notify();
  });
}

/** Vide la file — réservé aux TESTS. */
export function __resetDialogs(): void {
  queue = [];
  notify();
}

/** Rendu d'icône fourni par l'app (même contrat injecté que le toast : le nom est imposé, pas le dessin). */
export type DialogIconRenderer = (name: IconName, size: number, color: string) => ReactNode;

/** Couleurs résolues par le thème de l'app — cf. `DIALOG_COLOR_ROLES` (les rôles sont dans les tokens). */
export interface DialogPalette {
  overlay: string;
  card: string;
  title: string;
  message: string;
  tile: Record<'danger' | 'err' | 'warn' | 'info', { bg: string; fg: string }>;
  cancel: { bg: string; fg: string; border?: string };
  action: Record<'danger' | 'primary', { bg: string; fg: string }>;
}

export interface DialogHostProps {
  renderIcon: DialogIconRenderer;
  palette: DialogPalette;
}

/** Hôte des dialogues — à monter UNE fois près de la racine (comme `ToastHost`). */
export function DialogHost({ renderIcon, palette }: DialogHostProps) {
  const [current, setCurrent] = useState<DialogItem | null>(null);
  useEffect(() => subscribeDialogs(setCurrent), []);
  if (!current) return null;
  return <DialogCard key={current.id} item={current} renderIcon={renderIcon} p={palette} />;
}

function DialogCard({ item, renderIcon, p }: { item: DialogItem; renderIcon: DialogIconRenderer; p: DialogPalette }) {
  const isConfirm = item.kind === 'confirm';
  const tone: ConfirmTone | AlertTone = item.opts.tone ?? (isConfirm ? CONFIRM_DEFAULTS.tone : ALERT_DEFAULTS.tone);
  const danger = isConfirm && tone === 'danger';
  const iconName = isConfirm ? CONFIRM_ICON[tone as ConfirmTone] : ALERT_ICON[tone as AlertTone];
  const tileKey = tone === 'info' ? 'info' : tone === 'warn' ? 'warn' : isConfirm ? 'danger' : 'err';
  const actionLabel = isConfirm ? item.opts.confirmLabel! : (item.opts.closeLabel ?? ALERT_DEFAULTS.closeLabel);
  const actionRef = useRef<HTMLButtonElement>(null);
  const [shown, setShown] = useState(false);
  /** La recopie exigée pour les actions irréversibles — voir `requireText`. */
  const exige = isConfirm ? (item.opts.requireText ?? null) : null;
  const [saisi, setSaisi] = useState('');
  // ⚠️ Comparaison sur la valeur TAILLÉE : une espace finale collée par un copier-coller ferait échouer
  // une saisie juste, et l'opérateur ne verrait pas pourquoi. La casse, elle, compte — c'est le nom.
  const autorise = !exige || saisi.trim() === exige;

  useEffect(() => {
    const h = requestAnimationFrame(() => setShown(true));
    actionRef.current?.focus();
    // Clavier IMPOSÉ par l'hôte : Échap = non/fermer, Entrée = action (§24.1quater).
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); item.respond(false); }
      // ⚠️ « Entrée » NE CONFIRME PAS tant que la recopie n'est pas faite : sans ça, la protection
      // tomberait au premier appui machinal, ce qui la rendrait purement décorative.
      if (e.key === 'Enter' && autorise) { e.preventDefault(); item.respond(true); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => { cancelAnimationFrame(h); window.removeEventListener('keydown', onKey, true); };
  }, [item, autorise]);

  const btnBase = {
    height: DIALOG_BUTTONS.height, borderRadius: DIALOG_BUTTONS.radius, padding: `0 ${DIALOG_BUTTONS.padH}px`,
    fontSize: DIALOG_BUTTONS.fontSize, fontWeight: DIALOG_BUTTONS.fontWeight, cursor: 'pointer', border: 'none',
  } as const;

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      onClick={() => item.respond(false)}
      style={{
        position: 'fixed', inset: 0, zIndex: 1100, display: 'grid', placeItems: 'center',
        background: p.overlay, backdropFilter: `blur(${DIALOG_OVERLAY.blur}px)`,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: DIALOG_CARD.width, maxWidth: 'calc(100vw - 32px)', background: p.card,
          borderRadius: DIALOG_CARD.radius, padding: DIALOG_CARD.padding,
          // ⚠️ Pas d'`alignItems` : la colonne ÉTIRE ses enfants, donc la rangée de boutons couvre toute la largeur
          // et peut se ranger à droite ; la pastille, de largeur fixe, reste au bord gauche.
          display: 'flex', flexDirection: 'column', gap: DIALOG_CARD.gap, textAlign: DIALOG_CARD.align,
          opacity: shown ? 1 : 0,
          transform: `scale(${shown ? 1 : DIALOG_TIMING.enterScaleFrom})`,
          transition: `opacity ${DIALOG_TIMING.enterMs}ms ease-out, transform ${DIALOG_TIMING.enterMs}ms ease-out`,
        }}
      >
        <div style={{
          width: DIALOG_TILE.size, height: DIALOG_TILE.size, borderRadius: DIALOG_TILE.radius,
          background: p.tile[tileKey].bg, color: p.tile[tileKey].fg, display: 'grid', placeItems: 'center',
        }}>
          {renderIcon(iconName, DIALOG_TILE.iconSize, p.tile[tileKey].fg)}
        </div>
        <div style={{ fontSize: DIALOG_CARD.titleSize, fontWeight: DIALOG_CARD.titleWeight, color: p.title }}>{item.opts.title}</div>
        {!!item.opts.message && (
          <div style={{ fontSize: DIALOG_CARD.messageSize, lineHeight: DIALOG_CARD.messageLineHeight, color: p.message, whiteSpace: 'pre-wrap' }}>
            {item.opts.message}
          </div>
        )}
        {/* ⚠️ LA RECOPIE DU NOM — pour ce qui ne se rattrape pas. Un clic se donne par réflexe ; sur
            une boîte vue dix fois, la main confirme avant que l'œil n'ait lu. Recopier le nom de la
            CIBLE est le seul geste qui oblige à regarder ce qu'on détruit. */}
        {exige && (
          <label style={{ display: 'block', textAlign: 'left' }}>
            <span style={{ display: 'block', fontSize: 'var(--t-note)', color: 'var(--ink-600)', marginBottom: 6, lineHeight: 1.5 }}>
              
              <Trans k='Pour confirmer, recopiez <1>{exige}</1>' vars={{ exige }} parts={[(x) => <strong style={{ color: 'var(--ink-900)', fontFamily: 'var(--font-mono)' }}>{x}</strong>]} />
            </span>
            <input
              // Le focus va ICI et non au bouton : c'est le geste qu'on demande.
              ref={(el) => { if (el && document.activeElement !== el) el.focus(); }}
              value={saisi}
              onChange={(e) => setSaisi(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              aria-label={t('Recopiez {word} pour confirmer', { word: exige ?? '' })}
              style={{
                width: '100%', height: 38, padding: '0 11px', boxSizing: 'border-box',
                border: `1px solid var(--${autorise ? 'teal-400' : 'ink-200'})`,
                borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)',
                fontSize: 'var(--t-title)', outline: 'none',
              }}
            />
          </label>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: `flex-${DIALOG_BUTTONS.justify}`, gap: DIALOG_BUTTONS.gap, marginTop: DIALOG_BUTTONS.marginTop }}>
          {isConfirm && (
            <button
              type="button"
              onClick={() => item.respond(false)}
              style={{ ...btnBase, background: p.cancel.bg, color: p.cancel.fg, border: p.cancel.border ? `1px solid ${p.cancel.border}` : 'none' }}
            >
              {item.opts.cancelLabel ?? CONFIRM_DEFAULTS.cancelLabel}
            </button>
          )}
          <button
            ref={actionRef}
            type="button"
            // ⚠️ `aria-disabled` et non `disabled` : un bouton désactivé ne reçoit ni focus ni survol, et
            // ne peut donc RIEN dire de la raison. Ici il reste atteignable et porte son motif.
            aria-disabled={!autorise || undefined}
            title={autorise ? undefined : t('Recopiez « {word} » pour confirmer.', { word: exige ?? '' })}
            onClick={() => { if (autorise) item.respond(true); }}
            style={{
              ...btnBase,
              background: danger ? p.action.danger.bg : p.action.primary.bg,
              color: danger ? p.action.danger.fg : p.action.primary.fg,
              opacity: autorise ? 1 : 0.45,
              cursor: autorise ? 'pointer' : 'not-allowed',
            }}
          >
            {actionLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
